import * as React from 'react';

/**
 * Keeps a tooltip from flashing open when the dropdown or popover sharing its
 * trigger closes.
 *
 * Radix restores focus to the trigger on close (`onCloseAutoFocus` →
 * `triggerRef.focus()`) and `TooltipTrigger` opens immediately on focus — so
 * picking a menu item pops the trigger's tooltip with the pointer nowhere near
 * it. Clamping `open` while the overlay is open does not help: Radix dispatches
 * close-auto-focus from a `setTimeout(…, 0)`, a whole task after React has
 * re-rendered. So the tooltip has to ignore that one open request.
 *
 * Focus restoration is deliberately left intact — cancelling it (via
 * `event.preventDefault()`) would strand keyboard users on `<body>`.
 */
export const useOverlayTriggerTooltip = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ignoreFocusOpenRef = React.useRef(false);

  const onOpenChange = React.useCallback((open: boolean) => {
    if (open && ignoreFocusOpenRef.current) return;
    setIsOpen(open);
  }, []);

  const onCloseAutoFocus = React.useCallback(() => {
    // Radix focuses the trigger synchronously right after this handler returns,
    // inside the same task — a microtask lands after that focus-driven open
    // request but before any genuine hover or Tab, keeping the guard one-shot.
    ignoreFocusOpenRef.current = true;
    queueMicrotask(() => {
      ignoreFocusOpenRef.current = false;
    });
  }, []);

  return {
    /** Spread onto the `<Tooltip>` wrapping the overlay trigger. */
    tooltipProps: { open: isOpen, onOpenChange },
    /** Spread onto the overlay's content component. */
    contentProps: { onCloseAutoFocus },
  };
};
